export type SnapTarget = { x: number; y: number };

/**
 * Snap a point to the nearest candidate within a screen-space radius.
 * Distance is measured in source-image pixels: `radiusStagePx / stageScale`.
 * Returns the original point unchanged when no candidate is in range.
 */
export function snapPoint(
  point: SnapTarget,
  candidates: readonly SnapTarget[],
  radiusStagePx: number,
  stageScale: number,
): SnapTarget {
  const radiusSrc = radiusStagePx / stageScale;
  let best: SnapTarget | null = null;
  let bestDist = radiusSrc;
  for (const c of candidates) {
    const d = Math.hypot(point.x - c.x, point.y - c.y);
    if (d < bestDist) {
      best = c;
      bestDist = d;
    }
  }
  return best ?? point;
}

/**
 * 1-D variant of `snapPoint` for axis-aligned drags (dividers, crop edges).
 * Returns the original value when no candidate is within the screen-space radius.
 */
export function snapValue(
  value: number,
  candidates: readonly number[],
  radiusStagePx: number,
  stageScale: number,
): number {
  const radiusSrc = radiusStagePx / stageScale;
  let best = value;
  let bestDist = radiusSrc;
  for (const c of candidates) {
    const d = Math.abs(value - c);
    if (d < bestDist) {
      best = c;
      bestDist = d;
    }
  }
  return best;
}
